"use client";

import { useCallback, useEffect, useRef } from "react";
import classNames from "@/helpers/classNames";
import styles from "./styles.module.scss";

interface DualRangeSliderProps {
  min: number;
  max: number;
  valueMin: number;
  valueMax: number;
  step?: number;
  onChange: (min: number, max: number) => void;
  className?: string;
}

const DualRangeSlider = (props: DualRangeSliderProps) => {
  const { min, max, valueMin, valueMax, step = 1, onChange, className } = props;

  const sliderRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef<"min" | "max" | null>(null);

  const range = max - min || 1;
  const leftPercent = ((valueMin - min) / range) * 100;
  const widthPercent = ((valueMax - valueMin) / range) * 100;
  const maxPercent = ((valueMax - min) / range) * 100;

  useEffect(() => {
    if (trackRef.current) {
      trackRef.current.style.left = `${leftPercent}%`;
      trackRef.current.style.width = `${widthPercent}%`;
    }
  }, [leftPercent, widthPercent]);

  const getValueFromPosition = useCallback(
    (clientX: number) => {
      if (!sliderRef.current) {
        return min;
      }
      const rect = sliderRef.current.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const rawValue = min + percent * range;
      return Math.round(rawValue / step) * step;
    },
    [min, range, step],
  );

  const handlePointerDown = useCallback(
    (thumb: "min" | "max") => (e: React.PointerEvent) => {
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      draggingRef.current = thumb;
    },
    [],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!draggingRef.current) {
        return;
      }
      const val = getValueFromPosition(e.clientX);
      if (draggingRef.current === "min") {
        onChange(Math.min(val, valueMax), valueMax);
      } else {
        onChange(valueMin, Math.max(val, valueMin));
      }
    },
    [getValueFromPosition, onChange, valueMin, valueMax],
  );

  const handlePointerUp = useCallback(() => {
    draggingRef.current = null;
  }, []);

  const handleKeyDown = useCallback(
    (thumb: "min" | "max") => (e: React.KeyboardEvent) => {
      let newVal: number;
      const current = thumb === "min" ? valueMin : valueMax;
      if (e.key === "ArrowRight" || e.key === "ArrowUp") {
        newVal = current + step;
      } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
        newVal = current - step;
      } else {
        return;
      }
      e.preventDefault();
      newVal = Math.max(min, Math.min(max, newVal));
      if (thumb === "min") {
        onChange(Math.min(newVal, valueMax), valueMax);
      } else {
        onChange(valueMin, Math.max(newVal, valueMin));
      }
    },
    [valueMin, valueMax, step, min, max, onChange],
  );

  return (
    <div
      ref={sliderRef}
      className={classNames(styles.slider, className)}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      <div className={styles.track}>
        <div ref={trackRef} className={styles.activeTrack} />
      </div>
      <div
        className={styles.thumbHandle}
        style={{ left: `${leftPercent}%` }}
        role="slider"
        tabIndex={0}
        aria-label="Minimum price"
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={valueMin}
        onPointerDown={handlePointerDown("min")}
        onKeyDown={handleKeyDown("min")}
      >
        <span className={styles.gripLine} />
        <span className={styles.gripLine} />
        <span className={styles.gripLine} />
      </div>
      <div
        className={styles.thumbHandle}
        style={{ left: `${maxPercent}%` }}
        role="slider"
        tabIndex={0}
        aria-label="Maximum price"
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={valueMax}
        onPointerDown={handlePointerDown("max")}
        onKeyDown={handleKeyDown("max")}
      >
        <span className={styles.gripLine} />
        <span className={styles.gripLine} />
        <span className={styles.gripLine} />
      </div>
    </div>
  );
};

export default DualRangeSlider;
